import type { ReactNode } from "react"

import {
  BRAND_STORY_HEADLINE,
  BRAND_STORY_PILLARS,
  BRAND_STORY_SUMMARY,
} from "@/content/marketing/brand-story"

export { BRAND_STORY_HEADLINE, BRAND_STORY_PILLARS, BRAND_STORY_SUMMARY }

export const PRODUCT_TAGLINE =
  "Stop guessing what your product does — and start proving it, to every person and every AI agent on your team."

export const PRODUCT_SUMMARY =
  "ArtlessDev turns scenarios into a browsable spec. Developers, builders, and AI coding tools work from the same structured content; QA tracks whether behavior still matches; product owners read flows in plain language."

export const TRUST_STRIP_ITEMS = [
  "Free while availability is limited",
  "Structured, DB-backed scenarios",
  "MCP-ready for AI coding agents",
] as const

export const OWNERSHIP_FEATURES = [
  {
    id: "structured",
    headline: "Structured, not scattered",
    subheadline:
      "Every scenario lives in your workspace's own database, with a title, evidence, relationships, and status you can query — not prose spread across tickets and docs.",
    shot: "features" as const,
    wallpaper: "brown" as const,
    placement: "bottom-right" as const,
  },
  {
    id: "editing",
    headline: "Edit right in the app",
    subheadline:
      "Change a scenario's title, Conditions, Results, and relationships in the in-app editor. Every save is versioned, so a conflicting edit never silently overwrites someone else's work.",
    shot: "scenarios" as const,
    wallpaper: "purple" as const,
    placement: "bottom-left" as const,
  },
] as const

export const AGENT_FEATURES = [
  {
    id: "mcp-read",
    headline: "Search and read with MCP",
    subheadline:
      "Point a coding agent at the ArtlessDev MCP server to search scenarios and pull one scenario's full context — status, parent, chain links, cross-cutting usage — in a single call, instead of guessing from prose.",
    shot: "integrations" as const,
    wallpaper: "blue" as const,
    placement: "bottom-right" as const,
  },
  {
    id: "mcp-write",
    headline: "Propose drafts, report evidence",
    subheadline:
      "An agent can propose a new scenario draft or report a verification result backed by an artifact it actually observed — an HTTP response, a SQL row. Nothing becomes a real scenario or status change until a human confirms it.",
    shot: "scenarios" as const,
    wallpaper: "green" as const,
    placement: "bottom-left" as const,
  },
] as const

export const AUDIENCE_FEATURES = [
  {
    headline: "Product owners and stakeholders",
    subheadline:
      "Browse what the product actually does in Explore, grouped by feature and filterable by status or place. No ticket board required.",
    shot: "explore" as const,
  },
  {
    headline: "Professional developers",
    subheadline:
      "Keep scenarios in one structured, DB-backed workspace — edit in the app, or point a coding agent at the MCP server for that same access.",
    shot: "features" as const,
  },
  {
    headline: "Builders and vibe coders",
    subheadline:
      "Start a project and write scenarios right in the UI — no syntax or tooling to learn first.",
    shot: "start-in-app" as const,
  },
  {
    headline: "QA",
    subheadline:
      "Record a fit or a gap on each scenario, sweep the scenarios table after a release, and check activity history when behavior drifts from what's documented.",
    shot: "scenarios" as const,
  },
] as const

export const PRODUCT_MODEL_STATS = [
  {
    stat: "1 scenario",
    text: "A single plain-language fact about behavior — not a disposable ticket.",
  },
  {
    stat: "3 views",
    text: "Explore for narrative reading, Features for grouped lists, Scenarios for the table and inline edits.",
  },
  {
    stat: "2 status axes",
    text: "Draft or published, and checked or not — tracked independently in the app, separate from the scenario content itself.",
  },
] as const

export const SCENARIO_MODEL_PILLARS = [
  {
    title: "Nothing silent, nothing assumed",
    body: "A Scenario names every read it depends on and every write it makes — not just its parameters, but the session it assumes, the query it runs, the row it touches. Spell it out completely, and the dependency nobody knew was there finally surfaces.",
  },
  {
    title: "Artifacts, not intentions",
    body: "Words like “validates,” “handles,” and “ensures” describe what code is supposed to do — and intent can't be checked. Every step carries an artifact instead: the HTTP response, the SQL row, the element on screen. Nothing attached means exactly that: an idea, not yet a fact.",
  },
  {
    title: "One fact, every depth",
    body: "The button a customer clicks, the request the frontend fires, the row the backend writes — one Scenario, nested at finer and finer boundaries, not three separate documents drifting apart. Stakeholders and engineers end up pointing at the exact same object.",
  },
] as const

export type MarketingFaq = { id: string; question: string; answer: ReactNode }

export const MARKETING_FAQS: MarketingFaq[] = [
  {
    id: "faq-verification",
    question: "Does ArtlessDev verify my code automatically?",
    answer:
      "No — ArtlessDev doesn't run your tests or inspect your code. A person sets a scenario's status by hand, or an MCP-connected coding agent reports a one-off result backed by a concrete artifact it actually observed, like an HTTP response or a SQL row. A fit means someone checked and reality matched; the scenario's content stays the source of truth either way.",
  },
  {
    id: "faq-storage",
    question: "Where do scenarios live?",
    answer:
      "In your workspace's own database. You author each scenario directly in the app — title, Conditions and Results with evidence, relationships to other scenarios — or an MCP-connected coding agent proposes a draft for a human to confirm. There's no separate file to manage.",
  },
  {
    id: "faq-nesting",
    question: "How do nested scenarios and chains work?",
    answer:
      "Nesting (parent:) breaks one behavioral fact into child scenarios at a finer boundary — end-user down to module. Chains pass a specific output from one scenario into another as an input. Cross-cutting links show a shared scenario, like audit logging, wherever another scenario declares it. Explore renders all three inline as you read.",
  },
  {
    id: "faq-agents",
    question: "Can AI coding tools use my scenarios?",
    answer:
      "Yes. Connect a coding agent to the ArtlessDev MCP server to search scenarios and pull one scenario's full context — status, parent, chain links, cross-cutting usage — in a single call. From there an agent can propose a new scenario, which lands immediately in Draft status for a human to review, or report a verification result with evidence attached.",
  },
  {
    id: "faq-roles",
    question: "Who can browse, edit, or change status?",
    answer:
      "Workspace owners and admins manage billing and membership. Each project defaults to Admin and Member roles; Reader, Reviewer, and Editor roles are available where finer-grained control matters — a Reader can only browse, a Reviewer can change status, an Editor can also edit content. All tiers are free of charge while availability is limited.",
  },
]
